import { matchDuration, scoreBreakdown } from '@/game/sim';
import type { Frame } from '@/game/view/frame';
import { colorIndexFor, PLAYER_COLORS } from '@/game/view/palette';
import { formatAmounts, stockInUnits } from '@/game/view/resources';
import { useSimSelector } from './app/simFrame';

/** Float slack when comparing the summed match clock to its duration. */
const ELAPSED_EPSILON = 1e-6;

/** The frame once the match is over, else null (so a running match never re-renders this). */
const endedFrame = (frame: Frame) => (frame.view.status === 'ended' ? frame : null);

export function EndScreen() {
  const frame = useSimSelector(endedFrame);
  if (!frame) return null;
  const { view: state, playerId, playerOrder, playerName, rules } = frame;

  const winner = state.winner;
  const isVictory = winner === playerId;
  const winnerColorIdx = winner != null ? colorIndexFor(winner, playerOrder) : 0;
  const winnerColor = PLAYER_COLORS[winnerColorIdx];
  const showArmy = rules.match.timeout.army;
  // Stock counts at a fraction of what it would be worth spent (scoreBreakdown).
  const stockWeight = rules.match.timeout.stockWeight;
  // City levels count at a fraction of their cost (the main sink), so the
  // Buildings column holds them at that weight.
  const cityLevelWeight = rules.match.timeout.cityLevelWeight;
  const buildingsHint =
    cityLevelWeight === 1
      ? 'What standing buildings and their levels cost; a site counts as far as it is built'
      : `What standing buildings cost, with city levels counted ×${cityLevelWeight} of their cost; a site counts as far as it is built`;
  const partCell = 'py-1 pl-4 text-right tabular-nums text-fg-muted';
  // The match the host set up (null: no time limit) and how it ended.
  const duration = matchDuration(state, rules);
  // `elapsed` is summed tick by tick, so a match that ran out can end a hair
  // short of `duration` (float drift); the sim ends it with the same epsilon
  // (systems/victory.ts), so within it the match ran its full time.
  const timeUp = duration !== null && state.elapsed >= duration - ELAPSED_EPSILON;
  const played = timeUp ? duration : state.elapsed + ELAPSED_EPSILON;
  const clock = (secs: number) =>
    `${Math.floor(secs / 60)}:${String(Math.floor(secs % 60)).padStart(2, '0')}`;
  const howItEnded =
    state.endedBy === 'host'
      ? `Ended by the host at ${clock(played)}`
      : timeUp
        ? `Time's up at ${clock(played)}`
        : `${winner != null && !state.players[winner]?.eliminated ? 'Last player standing' : 'Over'} at ${clock(played)}${duration === null ? '' : ` of ${clock(duration)}`}`;

  return (
    <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="panel min-w-80 max-w-[calc(100vw-2rem)] overflow-x-auto bg-surface-strong p-6">
        <h2 className="text-center text-4xl font-bold tracking-[-0.04em]">
          {isVictory ? 'Victory' : 'Defeat'}
        </h2>
        <p className="mt-2 text-center text-sm" style={{ color: winnerColor }}>
          Winner: {winner != null ? playerName(winner) : '—'}
        </p>
        <p className="mt-1 text-center text-xs text-fg-subtle tabular-nums">
          {howItEnded}
          {state.match ? ` · map radius ${state.match.mapRadius} · seed ${state.match.seed}` : ''}
        </p>
        <table className="mt-5 w-full text-sm">
          <thead>
            <tr className="text-xs text-fg-subtle">
              <th className="py-1 text-left font-normal">Player</th>
              <th
                className="py-1 pl-4 text-right font-normal"
                title={
                  stockWeight === 1
                    ? 'Resources held in cities, one point each'
                    : `Resources held in cities count ×${stockWeight}: spending them on buildings or an army scores more`
                }
              >
                Stock{stockWeight === 1 ? '' : ` ×${stockWeight}`}
              </th>
              <th className="py-1 pl-4 text-right font-normal" title={buildingsHint}>
                Buildings{cityLevelWeight === 1 ? '' : '*'}
              </th>
              {showArmy && <th className="py-1 pl-4 text-right font-normal">Army</th>}
              <th className="py-1 pl-4 text-right font-normal">Score</th>
            </tr>
          </thead>
          <tbody>
            {playerOrder.map((pid) => {
              const pState = state.players[pid];
              const isElim = pState?.eliminated ?? false;
              const colorIdx = colorIndexFor(pid, playerOrder);
              const color = PLAYER_COLORS[colorIdx];
              // City stock + buildings still standing and their levels + the
              // army at cost (the timeout score).
              const score = scoreBreakdown(state, rules, pid);
              return (
                <tr key={pid}>
                  <td className="py-1">
                    <span className="flex items-center gap-1.5">
                      <span
                        className="h-2 w-2 flex-shrink-0 rounded-full"
                        style={{ background: color }}
                      />
                      <span title={pid} className={isElim ? 'text-fg-subtle line-through' : ''}>
                        {playerName(pid)}
                        {isElim ? ' (eliminated)' : ''}
                      </span>
                    </span>
                  </td>
                  {/* Every resource counts alike, metal too, times the stock weight. */}
                  <td
                    className={partCell}
                    title={`${formatAmounts(stockInUnits(state, pid)) || 'Nothing stored'}${
                      stockWeight === 1 ? '' : ` (×${stockWeight})`
                    }`}
                  >
                    {score.stock.toFixed(1)}
                  </td>
                  <td className={partCell} title={buildingsHint}>
                    {score.buildings.toFixed(1)}
                  </td>
                  {showArmy && <td className={partCell}>{score.army.toFixed(1)}</td>}
                  <td className="py-1 pl-4 text-right font-semibold tabular-nums">
                    {score.total.toFixed(1)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {cityLevelWeight !== 1 && (
          <p className="mt-2 text-xs text-fg-subtle">
            * City levels count ×{cityLevelWeight} of their cost.
          </p>
        )}
        <div className="mt-6 flex justify-center">
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="btn btn-primary"
          >
            Play again
          </button>
        </div>
      </div>
    </div>
  );
}
